import { Map } from "mapbox-gl";
import { getRadius } from "./getRadius";
import { generatePlumeGradient } from "./plumeGradient";

import {
  farmNeutralFill,
  farmNeutralStroke,
  farmInfectedFill,
  farmInfectedStroke,
  farmRadiusStroke,
  labelColor,
  stateStroke,
  plumeFill
} from "./colors";

import { MOBILE_WIDTH } from "./config";

import { AdjustedLabelConfig } from "./types";

// utils
function getCircleSize(windowWidth: number): number {
  return windowWidth <= MOBILE_WIDTH ? 4 : 6;
}

function getCountyLabelSize(windowWidth: number): number {
  return windowWidth <= MOBILE_WIDTH ? 14 : 16;
}

function getResponsiveCoords(
  desktopCoords: number[],
  mobileCoords: number[],
  windowWidth: number
): number[] {
  return windowWidth <= MOBILE_WIDTH ? mobileCoords : desktopCoords;
}

// add to map
export function addMapSource(map: Map, id: string, dataURL: string): void {
  map.addSource(id, {
    type: "geojson",
    data: dataURL
  });
}

function addStateLayer(map: Map, id: string): void {
  map.addLayer({
    id,
    type: "line",
    source: id,
    layout: {
      "line-cap": "round"
    },
    paint: {
      "line-color": stateStroke,
      "line-width": 3
    }
  });
}

function addFarmsBaseLayer(
  map: Map,
  source: string,
  id: string,
  windowWidth: number
): void {
  map.addLayer({
    id,
    type: "circle",
    source,
    paint: {
      "circle-color": farmNeutralFill,
      "circle-opacity": 0,
      "circle-radius": getCircleSize(windowWidth),
      "circle-stroke-width": 1,
      "circle-stroke-color": farmNeutralStroke,
      "circle-emissive-strength": 1
    }
  });
}

function addOtherInfectedFarms(
  map: Map,
  source: string,
  id: string,
  windowWidth: number
): void {
  map.addLayer({
    id,
    type: "circle",
    source,
    paint: {
      "circle-color": farmInfectedFill,
      "circle-opacity": 0,
      "circle-radius": getCircleSize(windowWidth),
      "circle-stroke-width": 0,
      "circle-stroke-color": farmInfectedStroke,
      "circle-emissive-strength": 1
    }
  });
}

function addInfectedFarms(
  map: Map,
  source: string,
  id: string,
  filter: string,
  windowWidth: number
): void {
  map.addLayer({
    id,
    type: "circle",
    source,
    filter: ["==", ["get", filter], true],
    paint: {
      "circle-color": farmNeutralFill,
      "circle-opacity": 0,
      "circle-radius": getCircleSize(windowWidth),
      "circle-stroke-width": 0,
      "circle-stroke-color": farmNeutralStroke,
      "circle-emissive-strength": 1
    }
  });
}

function addRadius(map: Map): void {
  const coordinates = getRadius();

  map.addSource("farm-radius", {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coordinates]
      },
      properties: {}
    }
  });

  map.addLayer({
    source: "farm-radius",
    id: "farm-radius",
    type: "line",
    paint: {
      "line-color": farmRadiusStroke,
      "line-width": 2,
      "line-dasharray": [2, 2],
      "line-opacity": 0
    }
  });
}

function addHoweLabel(map: Map, windowWidth: number): void {
  // Add labeled circle
  map.addSource("howe", {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [-84.694, 40.3]
      },
      properties: {
        title: "Howe's Hens"
      }
    }
  });

  map.addLayer({
    source: "howe",
    id: "howe-point",
    type: "circle",
    paint: {
      "circle-color": farmInfectedFill,
      "circle-stroke-color": farmInfectedStroke,
      "circle-stroke-width": 1,
      "circle-opacity": 0,
      "circle-stroke-opacity": 0,
      "circle-radius": getCircleSize(windowWidth),
      "circle-emissive-strength": 1
    }
  });

  map.addLayer({
    source: "howe",
    id: "howe-label",
    type: "symbol",
    layout: {
      "text-field": ["get", "title"],
      "text-anchor": "left",
      "text-offset": [1, 0],
      "text-font": ["Graphik Bold"]
    },
    paint: {
      "text-opacity": 0.9,
      "text-color": labelColor,
      "text-halo-color": "#000000",
      "text-halo-width": 1
    }
  });
}

function addCountyLabel(
  map: Map,
  county: string,
  coords: { desktop: number[]; mobile: number[] },
  windowWidth: number
): void {
  const { desktop, mobile } = coords;

  const coordinates = getResponsiveCoords(desktop, mobile, windowWidth);

  map.addSource(`${county}-label`, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: { type: "Point", coordinates },
      properties: { title: `${county}\nCOUNTY` }
    }
  });

  map.addLayer({
    source: `${county}-label`,
    id: `${county}-label`,
    type: "symbol",
    layout: {
      "text-transform": "uppercase",
      "text-field": ["get", "title"],
      "text-anchor": "center",
      "text-font": ["Graphik Regular"],
      "text-size": getCountyLabelSize(windowWidth),
      "text-letter-spacing": 0.05
    },
    paint: {
      // adjust Auglaize only
      "text-opacity": county === "Auglaize" ? 0 : 1,
      "text-color": "#999999"
    }
  });
}

function addPlumeLayer(map: Map, id: string, color: string): void {
  map.addLayer({
    source: id,
    id,
    type: "fill",
    layout: {},
    paint: {
      "fill-color": generatePlumeGradient(color),
      "fill-opacity": 0
    }
  });
}

// update layers
export function updateLayerSizesOnResize(map: Map, windowWidth: number): void {
  const newCircleSize = getCircleSize(windowWidth);

  const circleLayerIds = [
    "farms-base",
    "other-farms-base",
    "farms-within-plume",
    "infected-plume-farms",
    "farms-infected",
    "mercer-03",
    "howe-point"
  ];

  for (let i = 0; i < circleLayerIds.length; i++) {
    const circleLayerId = circleLayerIds[i];

    if (map.getLayer(circleLayerId)) {
      map.setPaintProperty(circleLayerIds[i], "circle-radius", newCircleSize);
    }
  }
}

export function updateCountyLabels(
  map: Map,
  windowWidth: number,
  stepType: "final" | "adjusted"
): void {
  // layerpositions to be adjusted on mobile
  const labelConfig: AdjustedLabelConfig = {
    "Jay-label": {
      adjusted: [-84.901, 40.469],
      final: [-85.00338, 40.434972],
      title: "JAY\nCOUNTY"
    },
    "Randolph-label": {
      adjusted: [-84.909, 40.137],
      final: [-85.005004, 40.164414],
      title: "RANDOLPH\nCOUNTY"
    }
  };

  if (windowWidth <= MOBILE_WIDTH) {
    for (const labelId in labelConfig) {
      if (map.getSource(labelId)) {
        const { adjusted, final, title } = labelConfig[labelId];
        const coordinates = stepType === "final" ? final : adjusted;

        (map.getSource(labelId) as any).setData({
          type: "Feature",
          geometry: { type: "Point", coordinates },
          properties: { title }
        });
      }
    }
  }
}

export function updateLabelsOnResize(map: Map, windowWidth: number): void {
  const countySize = getCountyLabelSize(windowWidth);

  const countyLabels = [
    "Darke-label",
    "Mercer-label",
    "Jay-label",
    "Randolph-label",
    "Adams-label",
    "Van Wert-label",
    "Auglaize-label"
  ];

  for (let i = 0; i < countyLabels.length; i++) {
    const countyLabel = countyLabels[i];
    if (map.getLayer(countyLabel)) {
      map.setLayoutProperty(countyLabels[i], "text-size", countySize);
    }
  }

  const countyCoords = [
    {
      name: "Darke",
      desktop: [-84.620438, 40.132176],
      mobile: [-84.620438, 40.064]
    },
    { name: "Mercer", coords: [-84.632059, 40.535333] },
    {
      name: "Jay",
      desktop: [-85.00338, 40.434972],
      mobile: [-84.901, 40.469]
    },
    {
      name: "Randolph",
      desktop: [-84.965, 40.18],
      mobile: [-84.909, 40.137]
    },
    { name: "Adams", coords: [-84.936131, 40.745733] },
    { name: "Van Wert", coords: [-84.585775, 40.85552] },
    { name: "Auglaize", coords: [-84.224018, 40.561309] }
  ];

  for (let i = 0; i < countyCoords.length; i++) {
    const { name, desktop, mobile, coords } = countyCoords[i];

    const layerLabel = `${name}-label`;

    if (map.getSource(layerLabel)) {
      const coordinates = desktop
        ? getResponsiveCoords(desktop, mobile, windowWidth)
        : coords;

      (map.getSource(`${name}-label`) as any).setData({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates
        },
        properties: {
          title: `${name}\nCOUNTY`
        }
      });
    }
  }
}

// load everything
export function loadDataLayers(map: Map, windowWidth: number): void {
  // add state lines
  addStateLayer(map, "ohio-indiana-state");

  // add all the farms
  addFarmsBaseLayer(map, "farms", "farms-base", windowWidth);
  addOtherInfectedFarms(map, "other-farms", "other-farms-base", windowWidth);

  // add plume
  addPlumeLayer(map, "plume", plumeFill);

  // add infected farms
  addInfectedFarms(
    map,
    "farms",
    "farms-within-plume",
    "within_plume",
    windowWidth
  );

  addInfectedFarms(
    map,
    "farms",
    "infected-plume-farms",
    "within_plume_infected_by_jan_20",
    windowWidth
  );

  addInfectedFarms(
    map,
    "farms",
    "farms-infected",
    "infected_ever",
    windowWidth
  );

  // add mercer-03 layer
  addInfectedFarms(map, "farms", "mercer-03", "highlight", windowWidth);

  // add misc. labels
  addHoweLabel(map, windowWidth);

  // add 20km radius circle
  addRadius(map);

  // add counties
  addCountyLabel(
    map,
    "Darke",
    { desktop: [-84.620438, 40.132176], mobile: [-84.620438, 40.064] },
    windowWidth
  );
  addCountyLabel(
    map,
    "Mercer",
    { desktop: [-84.632059, 40.535333], mobile: [-84.632059, 40.535333] },
    windowWidth
  );
  addCountyLabel(
    map,
    "Jay",
    { desktop: [-85.00338, 40.434972], mobile: [-84.901, 40.469] },
    windowWidth
  );
  addCountyLabel(
    map,
    "Randolph",
    { desktop: [-84.965, 40.18], mobile: [-84.909, 40.137] },
    windowWidth
  );
  addCountyLabel(
    map,
    "Adams",
    { desktop: [-84.936131, 40.745733], mobile: [-84.936131, 40.745733] },
    windowWidth
  );
  addCountyLabel(
    map,
    "Van Wert",
    { desktop: [-84.585775, 40.85552], mobile: [-84.585775, 40.85552] },
    windowWidth
  );
  addCountyLabel(
    map,
    "Auglaize",
    { desktop: [-84.224018, 40.561309], mobile: [-84.224018, 40.561309] },
    windowWidth
  );
}
